import React, { useState, useEffect, useCallback } from "react";
import plantService from "../services/plantService";
import lineService from "../services/lineService";
import machineService from "../services/machineService";
import masterDataService from "../services/masterDataService";
import authService from "../services/authService";
import productService from "../services/productService";
import skuService from "../services/skuService";
import MultiSelectDropdown from "./MultiSelectDropdown";

/**
 * ReportFilterBar
 *
 * Self-contained filter bar that fetches Plants, Lines, Machines, Shifts,
 * Products, and SKUs from the API, with cascading behaviour:
 *   Plant → filters Lines
 *   Line  → filters Machines
 *   Product → filters SKUs
 *
 * Props:
 *   selectedPlants, onPlantChange
 *   selectedLines,  onLineChange
 *   selectedMachines, onMachineChange
 *   selectedShifts, onShiftChange
 *   selectedProducts, onProductChange
 *   selectedSkus, onSkuChange
 */
const ReportFilterBar = ({
  selectedPlants = [],
  onPlantChange,
  selectedLines = [],
  onLineChange,
  selectedMachines = [],
  onMachineChange,
  selectedShifts = [],
  onShiftChange,
  selectedProducts = [],
  onProductChange,
  selectedSkus = [],
  onSkuChange,
  selectedLossTypes = null, // Optional
  onLossTypeChange = null,
  selectedLossSubTypes = null, // Optional
  onLossSubTypeChange = null,
}) => {
  const [plants, setPlants] = useState([]);
  const [allLines, setAllLines] = useState([]);
  const [allMachines, setAllMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [products, setProducts] = useState([]);
  const [allSkus, setAllSkus] = useState([]);
  const [allLossTypes, setAllLossTypes] = useState([]);
  const [allLossSubTypes, setAllLossSubTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Initial data load ───────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const tenantId = authService.getTenantId();
        const promises = [
          plantService.getAllPlants(),
          lineService.getAllLines(),
          machineService.getAllMachines(),
          masterDataService.getShifts(tenantId),
          productService.getAllProducts(),
          skuService.getAllSkus(),
        ];
        
        // Fetch Loss Types only if the props are provided
        if (selectedLossTypes !== null) {
          promises.push(masterDataService.getLossTypes(tenantId));
          promises.push(masterDataService.getLossSubTypes(tenantId));
        }

        const [
          plantsData, 
          linesData, 
          machinesData, 
          shiftsData, 
          productsData, 
          skusData, 
          lossTypesData, 
          lossSubTypesData
        ] = await Promise.all(promises);

        const pData = plantsData || [];
        setPlants(pData);

        // Auto-select plant ONLY for single-plant users
        if (selectedPlants?.length === 0 && pData.length === 1) {
          onPlantChange([pData[0].plantId]);
        }

        // Auto-select all lines ONLY for single-plant users (if no lines are yet selected)
        if (pData.length === 1 && selectedLines.length === 0) {
          const pId = pData[0].plantId;
          const plantLines = (linesData || []).filter(l => l.plantId === pId).map(l => l.lineId);
          if (plantLines.length > 0) {
            onLineChange(plantLines);
          }
        }
        setAllLines(linesData || []);
        setAllMachines(machinesData || []);
        setShifts(shiftsData || []);
        setProducts(productsData || []);
        setAllSkus(skusData || []);
        setAllLossTypes(lossTypesData || []);
        setAllLossSubTypes(lossSubTypesData || []);
      } catch (err) {
        console.error("ReportFilterBar: failed to load filter data", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedLossTypes !== null]);

  // ── Cascading: reset downstream selections when parents change ───────────────
  const handlePlantChange = useCallback(
    (vals) => {
      onPlantChange(vals);

      // Keep only lines that belong to the newly selected plants
      if (vals.length > 0) {
        const validLineIds = allLines
          .filter((l) => vals.includes(l.plantId))
          .map((l) => l.lineId);
        const filteredLines = selectedLines.filter((id) =>
          validLineIds.includes(id),
        );
        if (filteredLines.length !== selectedLines.length) {
          onLineChange(filteredLines);
        }
      } else {
        // No plant selected → keep existing line selection (show all lines)
      }
    },
    [onPlantChange, onLineChange, allLines, selectedLines],
  );

  const handleLineChange = useCallback(
    (vals) => {
      onLineChange(vals);
      // Keep only machines that belong to the newly selected lines
      if (vals.length > 0) {
        const validMachineIds = allMachines
          .filter((m) => vals.includes(m.lineId))
          .map((m) => m.machineId);
        const filteredMachines = selectedMachines.filter((id) =>
          validMachineIds.includes(id),
        );
        if (filteredMachines.length !== selectedMachines.length) {
          onMachineChange(filteredMachines);
        }
      }
    },
    [onLineChange, onMachineChange, allMachines, selectedMachines],
  );

  const handleProductChange = useCallback(
    (vals) => {
      if (onProductChange) onProductChange(vals);
      // Keep only SKUs that belong to the newly selected products
      if (vals.length > 0) {
        const validSkuIds = allSkus
          .filter((s) => vals.includes(s.productId))
          .map((s) => s.skuId);
        const filteredSkus = selectedSkus.filter((id) =>
          validSkuIds.includes(id),
        );
        if (onSkuChange && filteredSkus.length !== selectedSkus.length) {
          onSkuChange(filteredSkus);
        }
      } else {
        // No product selected → keep existing SKU selection (show all SKUs)
      }
    },
    [onProductChange, onSkuChange, allSkus, selectedSkus],
  );

  const handleLossTypeChange = useCallback(
    (vals) => {
      if (onLossTypeChange) onLossTypeChange(vals);
      // Keep only sub types that belong to the newly selected loss types
      if (vals.length > 0 && selectedLossSubTypes && onLossSubTypeChange) {
        const validSubTypeIds = allLossSubTypes
          .filter((s) => vals.includes(s.lossTypeId))
          .map((s) => s.subTypeId);
        const filteredSubTypes = selectedLossSubTypes.filter((id) =>
            validSubTypeIds.includes(id),
        );
        if (filteredSubTypes.length !== selectedLossSubTypes.length) {
          onLossSubTypeChange(filteredSubTypes);
        }
      }
    },
    [onLossTypeChange, onLossSubTypeChange, allLossSubTypes, selectedLossSubTypes]
  );

  // ── Derived options (cascade filtering) ─────────────────────────────────────
  const lineOptions =
    selectedPlants.length > 0
      ? allLines.filter((l) => selectedPlants.includes(l.plantId))
      : allLines;

  const machineOptions =
    selectedLines.length > 0
      ? allMachines.filter((m) => selectedLines.includes(m.lineId))
      : selectedPlants.length > 0
        ? allMachines.filter((m) =>
            allLines
              .filter((l) => selectedPlants.includes(l.plantId))
              .map((l) => l.lineId)
              .includes(m.lineId),
          )
        : allMachines;

  const skuOptions =
    selectedProducts.length > 0
      ? allSkus.filter((s) => selectedProducts.includes(s.productId))
      : allSkus;

  const lossSubTypeOptions =
    selectedLossTypes?.length > 0
      ? allLossSubTypes.filter((s) => selectedLossTypes.includes(s.lossTypeId))
      : allLossSubTypes;

  // ── Map shifts to have a consistent `name` field ─────────────────────────────
  const shiftOptions = shifts.map((s) => ({
    id: s.shiftId,
    name: `${s.shiftCode} (${s.startTime?.slice(0, 5)} – ${s.endTime?.slice(0, 5)})`,
  }));

  if (loading) {
    return (
      <>
        {["Plant", "Line", "Machine", "Shift", "Product", "SKU"].map((label) => (
          <div className="filter-group" key={label}>
            <label>{label}</label>
            <div
              className="filter-input"
              style={{
                display: "flex",
                alignItems: "center",
                color: "var(--text-tertiary)",
                fontSize: "0.85rem",
              }}
            >
              Loading…
            </div>
          </div>
        ))}
      </>
    );
  }

  return (
    <>
      <div className="filter-group">
        <label>Plant</label>
        <MultiSelectDropdown
          options={plants.map((p) => ({ id: p.plantId, name: p.plantName }))}
          selectedValues={selectedPlants}
          onChange={handlePlantChange}
          placeholder="All Plants"
          displayKey="name"
          valueKey="id"
        />
      </div>

      <div className="filter-group">
        <label>LINE</label>
        <MultiSelectDropdown
          options={lineOptions.map((l) => ({ id: l.lineId, name: l.lineName }))}
          selectedValues={selectedLines}
          onChange={handleLineChange}
          placeholder="All Lines"
          displayKey="name"
          valueKey="id"
        />
      </div>

      <div className="filter-group">
        <label>Machine</label>
        <MultiSelectDropdown
          options={machineOptions.map((m) => ({
            id: m.machineId,
            name: m.machineName,
          }))}
          selectedValues={selectedMachines}
          onChange={onMachineChange}
          placeholder={
            selectedLines.length > 0
              ? "All Machines in lines"
              : "All Machines"
          }
          displayKey="name"
          valueKey="id"
        />
      </div>

      <div className="filter-group">
        <label>Shift</label>
        <MultiSelectDropdown
          options={shiftOptions}
          selectedValues={selectedShifts}
          onChange={onShiftChange}
          placeholder="All Shifts"
          displayKey="name"
          valueKey="id"
        />
      </div>

      <div className="filter-group">
        <label>Product</label>
        <MultiSelectDropdown
          options={products.map((p) => ({ id: p.productId, name: p.productName }))}
          selectedValues={selectedProducts}
          onChange={handleProductChange}
          placeholder="All Products"
          displayKey="name"
          valueKey="id"
        />
      </div>

      <div className="filter-group">
        <label>SKU</label>
        <MultiSelectDropdown
          options={skuOptions.map((s) => ({ id: s.skuId, name: s.skuCode }))}
          selectedValues={selectedSkus}
          onChange={onSkuChange}
          placeholder={
            selectedProducts.length > 0
              ? "All SKUs in selected products"
              : "All SKUs"
          }
          displayKey="name"
          valueKey="id"
        />
      </div>

      {selectedLossTypes !== null && onLossTypeChange && (
        <div className="filter-group">
          <label>Loss Type</label>
          <MultiSelectDropdown
            options={allLossTypes.map((lt) => ({
              id: lt.lossTypeId,
              name: lt.lossTypeName,
            }))}
            selectedValues={selectedLossTypes}
            onChange={handleLossTypeChange}
            placeholder="All Loss Types"
            displayKey="name"
            valueKey="id"
          />
        </div>
      )}

      {selectedLossSubTypes !== null && onLossSubTypeChange && (
        <div className="filter-group">
          <label>Loss SubType</label>
          <MultiSelectDropdown
            options={lossSubTypeOptions.map((lst) => ({
              id: lst.subTypeId,
              name: lst.subTypeName,
            }))}
            selectedValues={selectedLossSubTypes}
            onChange={onLossSubTypeChange}
            placeholder={
              selectedLossTypes?.length > 0
                ? "All SubTypes in selected types"
                : "All SubTypes"
            }
            displayKey="name"
            valueKey="id"
          />
        </div>
      )}
    </>
  );
};

export default ReportFilterBar;
